import React, { useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { industries } from '../../data/industries';
import { leadershipFunctions } from '../../data/opportunities';
import { useInquiryForm } from '../../hooks/useInquiryForm';
import { buttonClasses } from '../../utils/buttonStyles';
import { compactErrors, cvError, emailError, requiredChoice, requiredText } from '../../utils/validation';
import { ConsentField } from './ConsentField';
import { FileUploadField } from './FileUploadField';
import { FormField } from './FormField';
import { FormStatusNotice } from './FormStatusNotice';
import { FormSuccess } from './FormSuccess';
import { HoneypotField } from './HoneypotField';
type CandidateValues = {
  fullName: string;
  email: string;
  position: string;
  functionalExpertise: string;
  preferredIndustry: string;
  geography: string;
  introduction: string;
  privacy: boolean;
};
const FORM_ID = 'candidate';
const INTRO_MAX = 2000;
const initialValues: CandidateValues = {
  fullName: '',
  email: '',
  position: '',
  functionalExpertise: '',
  preferredIndustry: '',
  geography: '',
  introduction: '',
  privacy: false
};
const functionOptions = [...leadershipFunctions.map(f => ({
  value: f,
  label: f
})), {
  value: 'Other',
  label: 'Other'
}];
const industryOptions = [{
  value: 'Open to multiple industries',
  label: 'Open to multiple industries'
}, ...industries.map(i => ({
  value: i.name,
  label: i.name
}))];
export function CandidateInquiryForm() {
  const [file, setFile] = useState<File | null>(null);
  const form = useInquiryForm<CandidateValues>({
    formId: FORM_ID,
    type: 'candidate',
    initialValues,
    file,
    mailSubject: 'Candidate introduction — Aurevane Search Partners',
    validate: v => compactErrors({
      fullName: requiredText(v.fullName, 'your full name', 2),
      email: emailError(v.email),
      position: requiredText(v.position, 'your current or most recent position'),
      functionalExpertise: requiredChoice(v.functionalExpertise, 'your functional expertise'),
      preferredIndustry: requiredChoice(v.preferredIndustry, 'a preferred industry'),
      geography: requiredText(v.geography, 'your geographic preferences'),
      introduction: requiredText(v.introduction, 'a short professional introduction', 40),
      cv: cvError(file),
      privacy: v.privacy ? undefined : 'Please confirm you have read the Privacy Policy.'
    }),
    summarize: v => [`Name: ${v.fullName}`, `Email: ${v.email}`, `Current or most recent position: ${v.position}`, `Functional expertise: ${v.functionalExpertise}`, `Preferred industry: ${v.preferredIndustry}`, `Geographic preferences: ${v.geography}`, '', 'Professional introduction:', v.introduction]
  });
  const {
    values,
    errors,
    status,
    setField
  } = form;
  const id = (key: keyof CandidateValues | 'cv') => `${FORM_ID}-${key}`;
  const handleReset = () => {
    setFile(null);
    form.reset();
  };
  if (status === 'success') {
    return <FormSuccess title="Thank you for introducing yourself." text="Your details have been received and will be held confidentially. A consultant will be in touch if your background aligns with a current or future search." resetLabel="Submit another introduction" onReset={handleReset} data-id="element-77" data-mp="1e71zdhoy93en" />;
  }
  return <form noValidate onSubmit={form.handleSubmit} className="relative space-y-6" data-id="element-78" data-mp="1sv6hokk3bcww">
      <p className="text-sm text-plum/60" data-id="element-79" data-mp="1xu9p2bnjlv81">
        Fields marked <span className="text-copper-deep" data-id="element-80" data-mp="1xwvx0cecicqs">*</span> are required. This is a registration of interest for current and future searches, not an application for a specific vacancy.
      </p>
      <HoneypotField id={`${FORM_ID}-website`} value={form.honeypot} onChange={form.setHoneypot} data-id="element-81" data-mp="26tn038rberuv" />
      <div className="grid gap-6 md:grid-cols-2" data-id="element-82" data-mp="7kpz2uwte13y">
        <FormField id={id('fullName')} label="Full Name" required autoComplete="name" value={values.fullName} onChange={v => setField('fullName', v)} error={errors.fullName} data-id="element-83" data-mp="cgwvnx3bm098" />
        <FormField id={id('email')} label="Email Address" type="email" required autoComplete="email" value={values.email} onChange={v => setField('email', v)} error={errors.email} data-id="element-84" data-mp="38e33avgjvpyc" />
        <FormField id={id('position')} label="Current or Most Recent Position" required autoComplete="organization-title" placeholder="e.g. Chief Operating Officer" value={values.position} onChange={v => setField('position', v)} error={errors.position} data-id="element-85" data-mp="3n8r3bu36lw6k" />
        <FormField id={id('functionalExpertise')} label="Functional Expertise" as="select" required options={functionOptions} value={values.functionalExpertise} onChange={v => setField('functionalExpertise', v)} error={errors.functionalExpertise} data-id="element-86" data-mp="2poo1uzwycytf" />
        <FormField id={id('preferredIndustry')} label="Preferred Industry" as="select" required options={industryOptions} value={values.preferredIndustry} onChange={v => setField('preferredIndustry', v)} error={errors.preferredIndustry} data-id="element-87" data-mp="2bvyear1rzlu7" />
        <FormField id={id('geography')} label="Geographic Preferences" required placeholder="e.g. Europe, Middle East, or flexible" value={values.geography} onChange={v => setField('geography', v)} error={errors.geography} data-id="element-88" data-mp="me19c0mdz783" />
        <div className="md:col-span-2" data-id="element-89" data-mp="cigaucgj0p33">
          <FormField id={id('introduction')} label="Short Professional Introduction" as="textarea" required rows={6} maxLength={INTRO_MAX} hint="Summarise your leadership experience, scope of responsibility, and the opportunities you would consider." value={values.introduction} onChange={v => setField('introduction', v)} error={errors.introduction} data-id="element-90" data-mp="hf6sv5bw5ur5" />
          <p className="mt-1.5 text-right text-xs text-plum/50" aria-live="polite" data-id="element-91" data-mp="3guv2db4xy9uk">
            {values.introduction.length} / {INTRO_MAX}
          </p>
        </div>
        <div className="md:col-span-2" data-id="element-92" data-mp="3kxrp9e1iks4b">
          <FileUploadField id={id('cv')} label="CV / Résumé" file={file} hint="PDF or Word, up to 5 MB. Your CV is kept private and is never published or shared without your agreement." onChange={f => {
          setFile(f);
          form.clearError('cv');
        }} error={errors.cv} data-id="element-93" data-mp="1rdga0rwium2j" />
        </div>
      </div>
      <ConsentField id={id('privacy')} checked={values.privacy} onChange={v => setField('privacy', v)} error={errors.privacy} purpose="to consider my background for relevant current and future searches" data-id="element-94" data-mp="18nrkjebfvxuk" />
      <FormStatusNotice status={status} message={form.message} mailtoHref={form.mailtoHref} attachmentNote={Boolean(file)} data-id="element-95" data-mp="ypamxyhzwkzj" />
      <button type="submit" disabled={status === 'submitting'} className={buttonClasses('primary', 'lg')} data-id="element-96" data-mp="2t337qo5orwmu">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-97" data-mp="2o4ibeztk1x3o" />
            Submitting…
          </> : 'Introduce Yourself'}
      </button>
    </form>;
}